"use client";

import { artistLinkRel } from "@/lib/seo-enrichment";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { MapPin, ExternalLink } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { Event, VenueWithEvents } from "@/lib/types";
import {
  formatDate,
  formatTime,
  formatPrice,
  getArtistNames,
  getEventImage,
  isDisplayableGenre,
} from "@/lib/utils";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { SubscribeButton } from "../subscribe-button";
import { EventGenreChip } from "../events/event-genre-chip";
import { useGenreTagLinkMode } from "../events/feed-genre-filter-context";
import { PlayIcon } from "@/components/spotify/play-icon";

const PAGE_SIZE = 10;

interface MapSidebarProps {
  events: Event[];
  venueGroups: VenueWithEvents[];
  blockedSpotifyIds: string[];
  citySlug: string;
}

export function MapSidebar({ events, venueGroups, blockedSpotifyIds, citySlug }: MapSidebarProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset visible count when events change (e.g. map panned)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleCount(PAGE_SIZE);
    scrollRef.current?.scrollTo(0, 0);
  }, [events]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, events.length));
  }, [events.length]);

  // IntersectionObserver to lazy-load more items
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const displayedEvents = events.slice(0, visibleCount);
  const hasMore = visibleCount < events.length;

  return (
    <div className="hidden lg:flex lg:w-96 shrink-0 flex-col border-l border-neutral-800 bg-dark">
      {/* Count header */}
      <div className="shrink-0 border-b border-neutral-800 px-4 py-3">
        <p className="font-mono text-xs text-neutral-400">
          {events.length} {events.length === 1 ? "SHOW" : "SHOWS"} ·{" "}
          {venueGroups.length} {venueGroups.length === 1 ? "VENUE" : "VENUES"}
        </p>
      </div>

      {/* Scrollable event list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar">
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="font-mono text-xs text-neutral-500">NO SHOWS IN THIS AREA</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-800/50">
            {displayedEvents.map((event) => (
              <SidebarEventItem key={event.id} event={event} blockedSpotifyIds={blockedSpotifyIds} citySlug={citySlug} />
            ))}
            {hasMore && (
              <div ref={sentinelRef} className="flex items-center justify-center py-3">
                <p className="font-mono text-[10px] text-neutral-600">LOADING MORE…</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarEventItem({ event, blockedSpotifyIds, citySlug }: { event: Event; blockedSpotifyIds: string[]; citySlug: string }) {
  const genreTagLinkMode = useGenreTagLinkMode();
  const t = useTranslations("Events");
  const locale = useLocale();
  const date = formatDate(event.local_date, locale, t("dateTba"));
  const time = formatTime(event.local_time, locale);
  const price = formatPrice(event.price_min, event.price_max, event.price_currency, locale, t("priceTba"));
  const { play, artistId: activeArtistId } = useSpotifyPlayer();

  const spotifyAttraction = event.attractions.find(
    (a) => a.spotify_id && !blockedSpotifyIds.includes(a.spotify_id)
  );
  const showSpotify = !!spotifyAttraction;
  const isPlaying = spotifyAttraction?.spotify_id === activeArtistId;

  const thumbnailUrl = getEventImage(event);
  const artistNames = getArtistNames(event);

  return (
    <div className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-neutral-800/50">
      {/* Artist circle */}
      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-neutral-700 bg-neutral-800">
        {thumbnailUrl ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={thumbnailUrl}
            alt={artistNames}
            className="h-full w-full object-cover"
            style={{ objectPosition: event.image_focus || "center" }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-mono text-xs text-neutral-400">
            {artistNames.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1 space-y-0.5">
        <h4 className="text-sm font-medium leading-snug text-white line-clamp-1">
          {event.attractions.length > 0
            ? event.attractions.map((a, i) => (
                <span key={a.id}>
                  {i > 0 && " / "}
                  <Link
                    href={`/artist/${a.slug}`}
                    rel={artistLinkRel(a)} prefetch={false}
                    className="hover:text-acid transition-colors"
                  >
                    {a.name}
                  </Link>
                </span>
              ))
            : event.name}
        </h4>
        <p className="font-mono text-[10px] text-neutral-400">
          {date}
          {time && <span className="text-neutral-600"> · {time}</span>}
        </p>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <MapPin className="h-2.5 w-2.5 shrink-0 text-neutral-500" />
            <Link
              href={`/${citySlug}/${event.venue.slug}`}
              className="text-[10px] text-neutral-500 line-clamp-1 hover:text-acid transition-colors"
            >
              {event.venue.name}
            </Link>
          </div>
          {(event.url || event.venue.url) ? (
            <a
              href={(event.url || event.venue.url) as string}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 flex items-center gap-1 font-mono text-[10px] text-neutral-500 hover:text-acid transition-colors"
            >
              {price}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          ) : event.price_min != null ? (
            <span className="shrink-0 font-mono text-[10px] text-neutral-500">
              {price}
            </span>
          ) : null}
        </div>
        {(() => {
            const g = event.attractions.flatMap((a) => a.genres ?? []).find((g) => isDisplayableGenre(g));
            return g ? (
              genreTagLinkMode === "directory" ? <EventGenreChip genre={g} citySlug={citySlug} pill /> : <span className="mt-0.5 inline-block rounded-full border border-acid/30 px-1.5 py-0 font-mono text-[9px] text-acid">
                {g.toUpperCase()}
              </span>
            ) : null;
          })()}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
        <SubscribeButton
          type="artist"
          targetId={event.attractions[0]?.id ?? 0}
          targetName={event.attractions[0]?.name ?? event.name}
          targetImage={thumbnailUrl}
          size="sm"
          event={event}
        />
        {showSpotify && (
          <button
            onClick={() => play(spotifyAttraction.spotify_id!, spotifyAttraction.name, { event, artistId: spotifyAttraction.id })}
            className={`shrink-0 flex h-8 w-8 items-center justify-center rounded-full transition-all ${
              isPlaying
                ? "bg-[#1DB954]/20 text-[#1DB954] shadow-[0_0_8px_rgba(29,185,84,0.3)]"
                : "text-neutral-400 hover:text-[#1DB954] hover:bg-neutral-700"
            }`}
            title={`Preview ${spotifyAttraction.name} on Spotify`}
          >
            <PlayIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
